import { z } from 'zod';

// Who wrote a change_healthcare_ids row: the creator in this service, or the
// member-migration importer carrying a backend2 row across.
export const CH_PATIENT_SOURCES = ['created', 'imported'] as const;
export type ChPatientSource = (typeof CH_PATIENT_SOURCES)[number];

// `pending` is the pre-CH claim; `active` is a patient Change Healthcare holds.
export const CH_PATIENT_STATUSES = ['pending', 'active'] as const;
export type ChPatientStatus = (typeof CH_PATIENT_STATUSES)[number];

// Upper bound on every Change Healthcare identifier column, shared by the DB
// length CHECKs and the payload schemas so they cannot drift.
export const CH_ID_MAX_LENGTH = 64;

// Pointer-shaped payload for the rx.ch_patient.* outbox events (ADR-052): the
// member id travels in the envelope actor, never in the payload. `.strict()`
// turns any extra field into a parse failure. `facilityId` may be the empty
// string, which is a legitimate imported value.
const rxChPatientPayloadShape = {
  chHsiValue: z.string().min(1).max(CH_ID_MAX_LENGTH),
  // Never null: a patient is created or adopted only once CH has named it.
  chPersonId: z.string().min(1).max(CH_ID_MAX_LENGTH),
  facilityId: z.string().max(CH_ID_MAX_LENGTH),
  childFacilityId: z.string().min(1).max(CH_ID_MAX_LENGTH).nullable(),
  appointmentId: z.string().uuid(),
};

export const rxChPatientCreatedPayloadSchema = z.object(rxChPatientPayloadShape).strict();
export type RxChPatientCreatedPayload = z.infer<typeof rxChPatientCreatedPayloadSchema>;

// An adoption may happen with no booking behind it (the sweep confirming a
// migrated member's imported patient), so its appointment pointer is nullable.
// The override is local to this schema: a patient is only ever created for a
// booking, so the created payload keeps the field required.
export const rxChPatientAdoptedPayloadSchema = z
  .object({ ...rxChPatientPayloadShape, appointmentId: z.string().uuid().nullable() })
  .strict();
export type RxChPatientAdoptedPayload = z.infer<typeof rxChPatientAdoptedPayloadSchema>;
